import { useContext, useEffect, useState } from 'react'

import Card from 'react-bootstrap/Card'

import EventDetailsContext from '../../../context/EventDetails/EventDetails'
import LoadingContext from '../../../context/Loading/Loading'
import UserContext from '../../../context/User/User'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'

import { getEvent } from '../../../utilities/api'
import { checkPermission } from '../../../utilities/helpers'
import AuthService from '../../../utilities/services/auth.service'
import useSafeAsync from '../../../utilities/useSafeAsync'

import { NoPermissionsContainer } from '../../NoPermissionsContainer'
import { PageLoadingContainer } from '../../PageLoadingContainer'
import { PublishingDisclaimer } from '../PublishingDisclaimer'
import { ConfigureMap } from './ConfigureMap'
import { InventoryProvider } from './InventoryProvider/InventoryProvider'
import { SelectMap } from './SelectMap'

export default function EventSeatMapWrapper({ eventId, offerId }) {
  const safeSetState = useSafeAsync()

  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)

  const { orgPermissions } = useContext(UserContext)

  const { getPermissions } = AuthService

  const {
    isEventPublishing,
    updateEvent,
    updateCanPublish,
    setStandardAdmissionOfferHasInventory,
  } = useContext(EventDetailsContext)

  const [step, setStep] = useState(1)

  const [event, setEvent] = useState()

  const [data, setData] = useState({})

  const [background, setBackground] = useState(null)

  const [activeTab, setActiveTab] = useState('scaling')

  const [isTemplate, setIsTemplate] = useState(false)

  // selected map id
  const [selectedMapId, setSelectedMapId] = useState(null)

  // all event templates
  const [templates, setTemplates] = useState([])

  // all event map configurations
  const [configurations, setConfigurations] = useState([])

  // selected map configuration - not template
  const [configuration, setConfiguration] = useState()

  // selected template
  const [template, setTemplate] = useState()

  const [isReloading, setIsReloading] = useState(false)

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.EVENT_MANAGEMENT
    )

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }

    showLoading()
    loadEvent(eventId).then((res) => {
      safeSetState(() => {
        // set all seat map templates from venue (venue-based templates)
        setTemplates(res?.data?.venue?.templates || [])
        // set all seat map configurations
        setConfigurations(res?.data?.room?.seatmaps)
        loadMapData(res?.data).then((res) => {
          // if there is map selected skip step 1 and go right to map configurations
          if (res?.mapId) setStep(2)
          hideLoading()
        })
      })
    })
  }, [eventId, orgPermissionsReady, hasPermission])

  useEffect(() => {
    // if map is template, get template from selected map id to get price levels/holds/kills
    if (isTemplate) {
      const template = templates?.find(
        (template) => template.id == selectedMapId
      )
      setTemplate(template)
    } else {
      setTemplate()
    }
  }, [templates, isTemplate, selectedMapId])

  // load/reload event -> reload event to set seatmap used when creating new offer and saving offer when leaving page
  // reloads Standard Admission offer inventory because event has new seatmap or template
  const loadEvent = (eventId) => {
    return new Promise((resolve, reject) => {
      getEvent(eventId)
        .then((res) => {
          safeSetState(() => {
            setEvent(res.data)
          })

          resolve(res)
        })
        .catch((err) => {
          console.error(err)
          reject()
        })
    })
  }

  // set map data, map id and configuration
  const loadMapData = (event) => {
    return new Promise((resolve) => {
      setData(event?.seatmap?.mapping)
      setBackground(event?.seatmap?.background)
      // get map id -> from event, meaning same event or changed to different event, or new event
      const selectedId = event?.seatmap?.id || undefined
      setSelectedMapId(selectedId)
      // set map configuration
      setConfiguration(event?.seatmap)
      resolve({ mapId: selectedId, data: event?.seatmap?.mapping })
    })
  }

  // Compute if all seats are assigned to price levels (matches MyEventPage logic)
  const computeStandardAdmissionOfferHasInventory = (eventData) => {
    if (
      !eventData?.offers?.length ||
      !eventData?.pricingLevels ||
      !eventData?.seatmap?.mapping
    ) {
      return false
    }
    const pricingLevels = Object.values(eventData.pricingLevels)
    const allPriceLevelSeats = pricingLevels.flatMap((pricing) => {
      const seats = Array.isArray(pricing?.seats) ? pricing.seats : []
      const gaSeats =
        Object.values(pricing?.gaSeats ?? {}).flatMap((s) => s) ?? []
      return [...seats, ...gaSeats]
    })
    const regularSeats = Object.keys(eventData.seatmap.mapping.seats ?? {})
    const gaSeats = Object.values(
      eventData.seatmap.mapping.sections ?? {}
    ).flatMap((s) => s?.spots ?? [])
    const totalSeats = [...regularSeats, ...gaSeats]
    return allPriceLevelSeats.length === totalSeats.length
  }

  // called every time map configuration changes
  const reloadData = () => {
    return new Promise((resolve) => {
      setIsReloading(true)
      loadEvent(eventId).then((res) => {
        const eventData = res?.data
        // set context when map configuration changes (e.g. after applying template)
        if (selectedMapId || eventData?.seatmap) {
          updateEvent(eventData)
          const hasInventory =
            computeStandardAdmissionOfferHasInventory(eventData)
          updateCanPublish(
            eventData?.status,
            eventData?.image?.id,
            hasInventory
          )
          setStandardAdmissionOfferHasInventory(hasInventory)
        }
        // get event map data, id and configuration
        loadMapData(res.data).then(() => {
          setActiveTab('scaling')
          setIsReloading(false)
          resolve(res)
        })
      })
    })
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer />
  }

  if (!hasPermission) {
    return <NoPermissionsContainer />
  }

  if (isLoading) {
    return <PageLoadingContainer />
  }

  // can be when event is set to publish or event is set to schedule
  if (isEventPublishing || event?.status === 'publishing') {
    return (
      <PublishingDisclaimer disclaimer="Cannot update map while event is publishing!" />
    )
  }

  return (
    <>
      {step === 1 ? (
        <SelectMap
          venueSlug={event?.venue?.slug}
          eventId={eventId}
          setStep={setStep}
          setIsTemplate={setIsTemplate}
          templates={templates}
          seatmaps={configurations}
          reloadData={reloadData}
        />
      ) : (
        <>
          {data && Object.values(data).length > 0 ? (
            <InventoryProvider activeTab={activeTab} eventId={eventId}>
              <ConfigureMap
                eventId={eventId}
                offerId={offerId}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                selectedMapId={selectedMapId}
                event={event}
                data={data}
                background={background}
                seatmaps={configurations}
                templates={templates}
                configuration={configuration}
                isReloading={isReloading}
                setIsReloading={setIsReloading}
                reloadData={reloadData}
              />
            </InventoryProvider>
          ) : (
            <PageLoadingContainer />
          )}
        </>
      )}
    </>
  )
}
